import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, LogOut, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { saveCredentials, disconnectAccount } from "@/lib/account";

export function AccountDialog({ open, onOpenChange, status, onStatusChange }) {
  const [barcode, setBarcode] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const configured = status?.configured;

  const handleConnect = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const next = await saveCredentials(barcode.trim(), pin.trim());
      onStatusChange(next);
      setBarcode("");
      setPin("");
      toast.success(`Library account connected${next.patronName ? ` — welcome, ${next.patronName}` : ""}`);
      onOpenChange(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setLoading(true);
    try {
      const next = await disconnectAccount();
      onStatusChange(next);
      toast.success("Library account disconnected");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="account-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Library Account
          </DialogTitle>
          <DialogDescription>
            {configured
              ? `Connected as ${status?.patronName || "your account"}. You can place holds directly from your results.`
              : "Connect your Alexandria Library card to place holds without leaving this page."}
          </DialogDescription>
        </DialogHeader>

        {configured ? (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-3 rounded-lg border border-primary/25 bg-primary/5 px-4 py-3">
              <div className="bg-primary/10 text-primary p-2 rounded-full">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground" data-testid="account-connected-name">
                  {status?.patronName || "Connected"}
                </p>
                <p className="text-xs text-muted-foreground">Holds will be picked up at Beatley Central.</p>
              </div>
            </div>
            <Button variant="outline" onClick={handleDisconnect} disabled={loading}
              className="w-full gap-2" data-testid="button-disconnect-account">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
              Disconnect account
            </Button>
          </div>
        ) : (
          <form onSubmit={handleConnect} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="barcode">Library card barcode</Label>
              <Input id="barcode" inputMode="numeric" autoComplete="off" placeholder="14-digit barcode"
                value={barcode} onChange={(e) => setBarcode(e.target.value)} data-testid="input-barcode" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pin">PIN / password</Label>
              <Input id="pin" type="password" autoComplete="off" placeholder="Your PIN"
                value={pin} onChange={(e) => setPin(e.target.value)} data-testid="input-pin" />
            </div>
            {error && (
              <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-md" data-testid="account-error">
                {error}
              </div>
            )}
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              Your credentials are encrypted and used only to talk to your library's catalog.
            </p>
            <DialogFooter>
              <Button type="submit" disabled={loading || !barcode.trim() || !pin.trim()}
                className="w-full gap-2" data-testid="button-connect-account">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Connect account
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
